import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dumbbell, Play, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useActiveWorkout, useTrainingRecords } from '@/hooks/useAppState';
import { WORKOUT_SESSION_TTL_MS } from '@/hooks/useActiveWorkout';
import { getCurrentUserId } from '@/lib/user-storage';
import { getCourseById } from '@/data/courses';
import { localTrainingDate, trainingRecordSource } from '@/lib/memberTrainingDay';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { workoutDraftKey } from './workoutDraft';
import { readBrowserWorkoutDraft, clearBrowserWorkoutDraft, performWorkoutDraftAction, WORKOUT_DRAFT_CHANGED } from './browserWorkoutDraft';
import { interruptedWorkoutRecord } from './interruptedWorkoutRecord';

export default function WorkoutResumeCard() {
  const navigate = useNavigate();
  const owner = getCurrentUserId();
  const { activeWorkout, endWorkout } = useActiveWorkout();
  const { addRecord } = useTrainingRecords();
  const [revision, refresh] = useState(0);
  const [endOpen, setEndOpen] = useState(false);
  const [ending, setEnding] = useState(false);
  const busy = useRef(false);
  useEffect(() => {
    const onChange = () => refresh(value => value + 1);
    const onStorage = (event: StorageEvent) => { if (!event.key || event.key === workoutDraftKey(owner)) onChange(); };
    const onDraft = (event: Event) => { if ((event as CustomEvent<string>).detail === owner) onChange(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener(WORKOUT_DRAFT_CHANGED, onDraft);
    if (!activeWorkout.active) return () => {
      window.removeEventListener('storage', onStorage); window.removeEventListener(WORKOUT_DRAFT_CHANGED, onDraft);
    };
    const expiry = window.setTimeout(onChange, Math.max(0, activeWorkout.startTimestamp + WORKOUT_SESSION_TTL_MS - Date.now() + 1));
    return () => {
      clearTimeout(expiry);
      window.removeEventListener('storage', onStorage); window.removeEventListener(WORKOUT_DRAFT_CHANGED, onDraft);
    };
  }, [owner, activeWorkout.active, activeWorkout.startTimestamp]);
  const draft = useMemo(() => readBrowserWorkoutDraft(owner, activeWorkout, WORKOUT_SESSION_TTL_MS),
    [owner, activeWorkout, revision]);
  const completedSets = draft?.records.reduce((sum, exercise) => sum + exercise.sets.filter(set => set.completed).length, 0) || 0;
  if (!draft) return null;
  const summary = !!draft.finishedRecordId;
  const current = draft.records[draft.exerciseIndex];
  const position = draft.feelingOpen ? '等待动作评价' : draft.resting ? '休息中'
    : `${current.exerciseName}第 ${draft.setIndex + 1} 组`;
  const finish = async (save: boolean) => {
    if (busy.current || getCurrentUserId() !== owner) return;
    busy.current = true; setEnding(true);
    try {
      const acquired = await performWorkoutDraftAction(owner, () => {
        if (getCurrentUserId() !== owner) return;
        const latest = readBrowserWorkoutDraft(owner, activeWorkout, WORKOUT_SESSION_TTL_MS);
        if (!latest) {
          setEndOpen(false); refresh(value => value + 1);
          toast.info('本次训练已结束或恢复时间已过'); return;
        }
        if (save) {
          const record = interruptedWorkoutRecord(latest, activeWorkout, localTrainingDate(), {
            courseName: activeWorkout.courseName || getCourseById(latest.courseId)?.name || '训练',
            dayName: activeWorkout.dayName,
          });
          if (record) { addRecord({ ...record, ...trainingRecordSource(latest.dayId) }); toast.success(`已保存 ${record.completedSets} 组记录`); }
        }
        clearBrowserWorkoutDraft(owner); endWorkout(); setEndOpen(false);
      });
      if (!acquired) toast.info('训练正在另一页面进行，请先关闭该页面或在那里结束训练');
    } catch { toast.error('暂时无法结束训练，请重试'); }
    finally { busy.current = false; setEnding(false); }
  };
  return <>
    <section aria-label="继续训练入口" className="metallic-card rounded-2xl p-4 mb-5 border border-primary/25">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          {summary ? <CheckCircle2 className="w-5 h-5" /> : <Dumbbell className="w-5 h-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-primary font-semibold mb-0.5">{summary ? '训练已完成' : '训练未结束'}</p>
          <h2 className="text-base font-bold text-foreground truncate">{activeWorkout.dayName}</h2>
          <p className="text-xs text-muted-foreground mt-1">{summary ? `已完成 ${completedSets} 组 · 查看本次总结`
            : `已完成 ${completedSets} 组 · ${position}`}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button className="flex-1 gap-1.5" onClick={() => {
          const latest = readBrowserWorkoutDraft(owner, activeWorkout, WORKOUT_SESSION_TTL_MS);
          if (latest) navigate(latest.route);
          else { refresh(value => value + 1); toast.info('本次训练已结束或恢复时间已过'); }
        }}><Play className="w-4 h-4 fill-current" />{summary ? '查看训练总结' : '继续训练'}</Button>
        {!summary && <button className="text-xs text-muted-foreground py-2" onClick={() => setEndOpen(true)}>结束本次</button>}
      </div>
    </section>
    <Dialog open={endOpen} onOpenChange={value => { if (!ending) setEndOpen(value); }}>
      <DialogContent className="max-w-[350px] rounded-2xl">
        <DialogHeader>
          <DialogTitle>结束本次训练？</DialogTitle>
          <DialogDescription>已完成 {completedSets} 组。可以保存已完成的记录，或放弃本次进度。</DialogDescription>
        </DialogHeader>
        <Button variant="secondary" disabled={ending} onClick={() => setEndOpen(false)}>暂不结束</Button>
        <div className="flex gap-2">
          <Button className="flex-1" variant="outline" disabled={ending} onClick={() => { void finish(false); }}>不保存并终止</Button>
          <Button className="flex-1" disabled={ending || completedSets === 0} onClick={() => { void finish(true); }}>保存并终止</Button>
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
